import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import StatusIndicator from "./shared/StatusIndicator";
import RaceWaitingIndicator from "./shared/RaceWaitingIndicator";
import EmojiPenaltyNote from "./shared/EmojiPenaltyNote";
import type { EmojiPenaltyResponse, WaitingRoomRacer } from "../api/types";

/**
 * How many blank "prompt" lines show for each pipeline step (step names mirror
 * STEP_LABELS in the backend's normalizeStatus.ts). The prompt itself is never
 * sent to the page, so the lines are placeholders: one more appears as each
 * prompt step starts, and once painting begins they stay, faded. Unknown or
 * missing steps show none.
 */
const PROMPT_LINES: Record<string, number> = {
  gatherContext: 1,
  conceptDirection: 2,
  synthesizeAndTitle: 3,
  // legacy step names (pre-4.0 docs / in-flight resumes)
  interpretEmojis: 2,
  artDirection: 2,
  criticRefine: 2,
  synthesizePrompt: 3,
  generateTitle: 3,
};

const PAINTING_STEPS = new Set(["generateImage", "postTweet", "announcements", "finalize"]);

const PROMPT_LINE_WIDTHS = ["90%", "74%", "52%"];

/** Compact run-time format for the elapsed indicator: "42s", "1m 20s". */
function formatElapsed(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, "0")}s` : `${s}s`;
}

const sheen = keyframes`
  0%   { background-position: 160% 0, 0 0; }
  100% { background-position: -60% 0, 0 0; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 0 1px rgba(0,0,0,0.06), 0 8px 26px rgba(0,0,0,0.10); }
  50%      { box-shadow: 0 0 0 1px rgba(102,51,153,0.30), 0 8px 30px rgba(102,51,153,0.22); }
`;

const dot = keyframes`
  0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
  40%           { opacity: 1;    transform: translateY(-2px); }
`;

const Panel = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 460px;
  margin: 0 auto;
  padding: 4px 0 8px;
`;

const StatusRow = styled.div`
  min-height: 2.2em;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

/**
 * The blank prompt lines under the status. Fixed height for all three lines,
 * so a new line never moves the blocks below it.
 */
const PromptLines = styled(motion.div)`
  width: min(240px, 66vw);
  height: 58px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
`;

const breathe = keyframes`
  0%, 100% { opacity: 0.45; }
  50%      { opacity: 1; }
`;

const PromptLine = styled(motion.div)`
  height: 8px;

  & > span {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    background: #d8cdee;
    animation: ${breathe} 2.4s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    & > span {
      animation: none;
    }
  }
`;

/** Opens the square from zero height when the first preview frame lands. */
const CanvasReveal = styled(motion.div)`
  overflow: hidden;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  width: 100%;
`;

/**
 * The square the art paints into. It only exists once a live preview frame
 * has arrived (OpenAI sends between zero and a few per run), so a run without
 * frames never shows an empty box.
 */
const Canvas = styled.div<{ $live: boolean }>`
  position: relative;
  width: min(300px, 78vw);
  aspect-ratio: 1 / 1;
  border-radius: 14px;
  overflow: hidden;
  background:
    linear-gradient(110deg, transparent 30%, rgba(255, 255, 255, 0.55) 50%, transparent 70%) 0 0 / 220% 100%,
    linear-gradient(160deg, #f3eefa 0%, #ece8f2 55%, #e9e4ee 100%);
  animation: ${sheen} 3.2s ease-in-out infinite, ${glow} 2.4s ease-in-out infinite;
  animation-play-state: ${(p) => (p.$live ? "running" : "paused")};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const PreviewImg = styled(motion.img)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const LiveChip = styled(motion.span)`
  position: absolute;
  z-index: 100;
  left: 10px;
  bottom: 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(17, 17, 17, 0.62);
  color: #fff;
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Dots = styled.span`
  display: inline-flex;
  gap: 3px;

  span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: currentColor;
    display: inline-block;
    animation: ${dot} 1.4s ease-in-out infinite;
  }
  span:nth-child(2) {
    animation-delay: 200ms;
  }
  span:nth-child(3) {
    animation-delay: 400ms;
  }
`;

const StepBlock = styled(motion.div)`
  width: min(300px, 78vw);
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const Elapsed = styled.span`
  font-size: 0.7rem;
  color: #999;
  margin-left: 6px;
  font-variant-numeric: tabular-nums;
`;

const Track = styled.div`
  width: 100%;
  height: 4px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
`;

const Fill = styled(motion.div)`
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #9333ea, #663399);
`;

const StepMeta = styled.span`
  font-size: 0.72rem;
  color: #888;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
`;

const Flavour = styled(motion.p)`
  margin: 14px 0 0;
  min-height: 2.4em;
  max-width: 420px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #666;
  text-align: center;
`;

const WaitingDots: React.FC = () => (
  <Dots aria-hidden>
    <span />
    <span />
    <span />
  </Dots>
);

/**
 * Subtle time-in-step readout next to the step label. The WS step heartbeat
 * carries `elapsedMs` = time spent inside the current step; we keep counting
 * locally so the long WS-silent image-gen step (~100s) doesn't look stalled,
 * and re-anchor whenever a fresh value arrives.
 */
const ElapsedIndicator: React.FC<{ elapsedMs: number }> = ({ elapsedMs }) => {
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  const anchorRef = useRef<{ elapsedMs: number; at: number }>({ elapsedMs, at: Date.now() });
  if (anchorRef.current.elapsedMs !== elapsedMs) {
    anchorRef.current = { elapsedMs, at: Date.now() };
  }
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const shownMs = anchorRef.current.elapsedMs + Math.max(0, nowMs - anchorRef.current.at);
  return <Elapsed>· {formatElapsed(shownMs)}</Elapsed>;
};

/**
 * Live preview frames, cross-faded. Each new partial stacks on top and fades
 * in (blur to sharp) while the previous frame stays fully opaque underneath;
 * the older one is dropped once the fade completes. Fading both at once (an
 * AnimatePresence exit) blanked the canvas for a moment on every frame.
 */
const PreviewStack: React.FC<{ b64: string; index: number }> = ({ b64, index }) => {
  const seqRef = useRef(0);
  const lastRef = useRef({ index, b64 });
  const [frames, setFrames] = useState<Array<{ key: number; b64: string }>>(() => [
    { key: 0, b64 },
  ]);
  useEffect(() => {
    if (lastRef.current.index === index && lastRef.current.b64 === b64) return;
    lastRef.current = { index, b64 };
    seqRef.current += 1;
    const key = seqRef.current;
    setFrames((prev) => [...prev.slice(-1), { key, b64 }]);
  }, [index, b64]);

  return (
    <>
      {frames.map((f, i) => (
        <PreviewImg
          key={f.key}
          src={`data:image/png;base64,${f.b64}`}
          alt="today's art, still being painted"
          style={{ zIndex: 1 + i }}
          initial={{ opacity: 0, filter: "blur(10px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          onAnimationComplete={() => {
            if (i === frames.length - 1 && frames.length > 1) setFrames((fs) => fs.slice(-1));
          }}
        />
      ))}
    </>
  );
};

const swap = {
  initial: { opacity: 0, y: 4 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.28, ease: "easeOut" },
} as const;

/**
 * The waiting screen while today's AOTD is painted. Top to bottom:
 *   1. The status badge (glowing letters) with the current pipeline step.
 *   2. Blank prompt lines that fill in over the prompt steps; once a live
 *      preview frame arrives they give way to the square, which opens from
 *      zero height and cross-fades frame to frame as the art sharpens.
 *   3. A progress bar, then "step N of M" + time in step.
 *   3. The racer strip ("N users think they got the aotd", +450ms tags).
 *   4. The viewer's own no-emoji penalty, if they raced with one.
 *   5. A rotating flavour line.
 * Every block animates in and out, so new information never jumps the page.
 */
export interface AotdGeneratingPanelProps {
  generationStatus?: string;
  phase?: string;
  participantCount?: number;
  participantEmojis?: string[];
  racers?: WaitingRoomRacer[];
  message?: string;
  stepLabel?: string;
  stepName?: string;
  stepIndex?: number;
  totalSteps?: number;
  percent?: number;
  /** Time spent inside the current step at the latest WS heartbeat. Absent
   *  when only REST hydration is available — the indicator doesn't render. */
  stepElapsedMs?: number;
  /** Live partial-image preview of today's art (base64 PNG), streamed over WS. */
  previewB64?: string;
  previewIndex?: number;
  /** The viewer's no-emoji penalty, for the "+450ms was added" note. */
  emojiPenalty?: EmojiPenaltyResponse;
}

export const AotdGeneratingPanel: React.FC<AotdGeneratingPanelProps> = ({
  generationStatus,
  phase,
  participantCount,
  participantEmojis,
  racers,
  message,
  stepLabel,
  stepName,
  stepIndex,
  totalSteps,
  percent,
  stepElapsedMs,
  previewB64,
  previewIndex,
  emojiPenalty,
}) => {
  const hasPreview = typeof previewB64 === "string" && previewB64.length > 0;
  const isActiveGeneration = ["generating", "busy", "resuming"].includes(generationStatus ?? "");
  const reduceMotion = useReducedMotion();
  // While painting, the glowing letters show the fine step label ("synthesizing
  // the prompt"); otherwise the coarse phase or status, as before.
  const showStepLabel = isActiveGeneration && Boolean(stepLabel);
  const indicatorStatus = showStepLabel ? "generating" : (generationStatus ?? "generating");
  const statusText = showStepLabel ? stepLabel : phase;
  const isPainting = PAINTING_STEPS.has(stepName ?? "");
  const promptLineCount = isPainting
    ? PROMPT_LINE_WIDTHS.length
    : (PROMPT_LINES[stepName ?? ""] ?? 0);
  const hasStepProgress =
    typeof stepIndex === "number" && typeof totalSteps === "number" && totalSteps > 0;
  const pct =
    typeof percent === "number" ? Math.max(0, Math.min(100, Math.round(percent))) : null;
  const stepMeta = hasStepProgress
    ? `step ${Math.min(stepIndex! + 1, totalSteps!)} of ${totalSteps}`
    : pct !== null
      ? `${pct}%`
      : null;

  return (
    <Panel layout>
      <StatusRow>
        <AnimatePresence initial={false} mode="wait">
          <motion.div key={statusText ?? indicatorStatus} {...swap}>
            <StatusIndicator
              status={indicatorStatus}
              overlay={false}
              message={undefined}
              phase={statusText}
            />
          </motion.div>
        </AnimatePresence>
      </StatusRow>

      <AnimatePresence initial={false} mode="wait">
        {hasPreview ? (
          <CanvasReveal
            key="canvas"
            initial={reduceMotion ? false : { height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Canvas $live={isActiveGeneration}>
              <PreviewStack b64={previewB64!} index={previewIndex ?? 0} />
              <LiveChip {...swap}>
                live preview
                <WaitingDots />
              </LiveChip>
            </Canvas>
          </CanvasReveal>
        ) : isActiveGeneration ? (
          <PromptLines
            key="lines"
            aria-hidden
            animate={{ opacity: isPainting ? 0.35 : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {PROMPT_LINE_WIDTHS.slice(0, promptLineCount).map((width) => (
              <PromptLine
                key={width}
                initial={reduceMotion ? false : { width: 0 }}
                animate={{ width }}
                transition={{ duration: 1.2, ease: "easeOut" }}
              >
                <span />
              </PromptLine>
            ))}
          </PromptLines>
        ) : null}
      </AnimatePresence>

      <StepBlock layout>
        {pct !== null && isActiveGeneration ? (
          <Track
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-label="generation progress"
          >
            <Fill
              initial={false}
              animate={{ width: `${Math.max(pct, 3)}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />
          </Track>
        ) : null}
        {stepMeta ? (
          <StepMeta>
            {stepMeta}
            {typeof stepElapsedMs === "number" && <ElapsedIndicator elapsedMs={stepElapsedMs} />}
          </StepMeta>
        ) : null}
      </StepBlock>

      <RaceWaitingIndicator
        active={isActiveGeneration}
        participantCount={participantCount}
        participantEmojis={participantEmojis}
        racers={racers}
      />

      <EmojiPenaltyNote penalty={emojiPenalty} moment="today" />

      <AnimatePresence initial={false} mode="wait">
        {isActiveGeneration && message ? (
          <Flavour key={message} {...swap}>
            {message}
            <span style={{ marginLeft: 6, color: "#aaa" }}>
              <WaitingDots />
            </span>
          </Flavour>
        ) : null}
      </AnimatePresence>
    </Panel>
  );
};

export default AotdGeneratingPanel;
